<template>
  <v-container>
    <MissingParamsDialog ref="missingParamsDialog" @mustSaveMissingParams="mustSaveMissingParams = true" />
    <PageHeader icon="assignment" no-padding subtitle="Use this query tool to compile data from multiple discrete data stations. The parameters will update a short time after selection." />

    <v-card>
      <v-card-actions class="card-bg-2 py-0 layout wrap">
        <v-btn class="my-2 ml-0 mr-2" style="min-width: 160px" :depressed="drawer" @click="drawer = true">
          <v-icon class="mr-2" color="info">folder_open</v-icon>
          <div class="text-capitalize">{{$t('Button.Open')}}</div>
        </v-btn>
        <v-divider v-if="$vuetify.breakpoint.mdAndUp" class="my-2 ml-1 mr-3" vertical />
        <NewQuery class="my-2 ml-0 mr-2" :confirmOnNew="hasUnsavedChanges" :disabled="!canNewQuery" @clear:query="clearQuery()" />
        <SaveQuery class="my-2 ml-0 mr-2" :query="query" :disabled="!canSave" @update:query="updateQuery($event)" :selection="selection" :reportOptions="reportOptions" />

        <v-divider v-if="$vuetify.breakpoint.mdAndUp" class="my-2 ml-1 mr-3" vertical />
        <!-- <Download class="my-2 ml-0 mr-2" :disabled="!canDownload" :selection="selection" :reportOptions="reportOptions" @loading="isGeneratingReport = $event" />

        <v-divider v-if="$vuetify.breakpoint.mdAndUp" class="my-2 ml-1 mr-3" vertical /> -->
        <AsyncDownload class="my-2 ml-0 mr-2" :disabled="!canAsyncDownload" :selection="selection" :reportOptions="reportOptions" @loading="isSubmittingAsyncGeneratingRequest = $event" />
      </v-card-actions>
      <v-divider />

      <v-layout>
        <SavedQueries ref="savedQueries" :confirmOnLoad="hasUnsavedChanges" @update:drawer="drawer = $event" :query="query" @load:query="loadQuery($event)" :drawer="drawer" />
        <v-flex class="pt-0" style="min-width: 0;">
          <QueryHeader :class="{ 'card-border-left': drawer }" :query="query" />
          <v-divider />
          <AlertsBar class="pb-1" :mustSaveMissingParams="mustSaveMissingParams" @showMissingParams="$refs.missingParamsDialog.show()" :hasSelectedStation="hasSelectedStation" :hasSelectedAnalyte="hasSelectedAnalyte" :hasMadeSelection="hasMadeSelection" :isQuerySaved="isQuerySaved" :hasQuery="!!query" :hasUnsavedChanges="hasUnsavedChanges" />
          <v-card flat :class="{ 'grey--text': loading }">
            <v-card-title class="card-bg-2 headline">
              <v-flex style="align-items: baseline; display: flex;">
                Media Types
                <v-menu v-model="showMediaTypesHelp" offset-y open-on-hover :open-delay="400">
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" @click="showMediaTypesHelp = true" class="clickable ml-2" color="primary">
                      info_outline
                    </v-icon>
                  </template>
                  <v-card>
                    <v-card-text>
                      Filter stations and results by the selected media types.
                    </v-card-text>
                  </v-card>
                </v-menu>
              </v-flex>
            </v-card-title>
            <v-card-text class="pt-0">
              <v-autocomplete
                :items="allMediaTypes"
                v-model="selectedMediaTypes"
                multiple
                chips
                deletable-chips
                :disabled="loading"
                @change="applyMediaTypes"
                hide-details
                solo
                prepend-icon="filter_list"
              />
            </v-card-text>
          </v-card>
          <v-divider />
          <v-tabs :height="32" v-model="activeTab" slider-color="info" color="transparent">
            <v-tab class="selection-tab mx-1 mt-1" :href="`#${availableTabs.parameters}`">
              Report data
            </v-tab>
            <v-tab class="selection-tab mx-1 mt-1" :href="`#${availableTabs.actions}`">
              Report options
            </v-tab>
            <v-tab-item :class="{ 'card-border-left': drawer }" :value="availableTabs.parameters">
              <v-divider />
              <v-progress-linear v-if="loading" class="position-sticky my-0" :height="10" color="info" indeterminate />
              <div v-else class="card-bg-2" style="width: 100%; height: 10px;" />
              <ViewQuery ref="viewQuery" style="margin-top: -10px;" :isGeneratingReport="isGeneratingReport" @update:isInitialized="isInitialized = $event" @update:loading="loadingStates = $event" @update:selection="updateSelection($event)" @update:allMediaTypes="allMediaTypes = $event" />
            </v-tab-item>
            <v-tab-item :value="availableTabs.actions">
              <v-divider />
              <ReportParams ref="reportParams" :disabled="loading" @update:reportOptions="reportOptions = $event" :selection="selection" />
            </v-tab-item>
          </v-tabs>
        </v-flex>
      </v-layout>
    </v-card>
  </v-container>
</template>

<script>
  import MissingParamsDialog from './MissingParamsDialog'
  import ViewQuery from './ViewQuery'
  import ReportParams from './Parameters/ReportParams'

  //import Download from './Actions/Download/Download'
  import AsyncDownload from './Actions/Download/AsyncDownload'
  import SaveQuery from './Actions/SaveQuery/SaveQuery'
  import NewQuery from './Actions/NewQuery'

  import SavedQueries from './SavedQueries'
  import AlertsBar from './AlertsBar'
  import QueryHeader from './QueryHeader'

  const availableTabs = {
    parameters: 'params',
    actions: 'actions',
  }

  import {
    emptyQuery,
  } from '@/helpers/multiStationQuery'

  import { debounce } from '@/helpers/performance'

  const serviceUnavailable = (context) => context.$store.dispatch('app/setSnackbar', {
    text: 'Sorry, saved queries are not available right now. Please try again in a few minutes — let us know if it happens again!',
    color: 'error',
    icon: 'error',
  })

  import {
    PING_INTERVAL,
  } from '@/store/ServiceRegistry/services'

  export default {
    components: {
      MissingParamsDialog,
      ViewQuery,
      ReportParams,
      //Download,
      AsyncDownload,
      SaveQuery,
      NewQuery,
      SavedQueries,
      AlertsBar,
      QueryHeader,
    },
    data() {
      return {
        availableTabs,
        activeTab: availableTabs.parameters,

        isInitialized: false,
        reportOptions: null,
        isGeneratingReport: false,
        isSubmittingAsyncGeneratingRequest: false,
        query: null,
        isQuerySaved: true,
        loadingStates: {},
        selection: null,
        drawer: false,
        search: '',

        mustSaveMissingParams: false,
        allMediaTypes: [],
        selectedMediaTypes: [],
        showMediaTypesHelp: false,

        headers: [{
          text: 'Name',
          value: 'name',
          align: 'left',
        },
        {
          text: 'Description',
          value: 'description',
          align: 'left',
        },
        {
          text: 'Last updated',
          value: 'lastUpdated',
          align: 'left',
        },
        {
          text: 'Actions',
          value: '',
          sortable: false,
          align: 'center',
        },
        ],
        pingTimeoutId: null,
      }
    },
    computed: {
      isServiceActive() {
        return this.$store.getters['multistation/isServiceActive']
      },
      missingParams() {
        return this.$store.getters['multistation/missingParams']
      },
      hasSelectedAnalyte() {
        return this.selection && this.selection.analytes && this.selection.analytes.length > 0
      },
      hasSelectedStation() {
        return this.selection && this.selection.stations && this.selection.stations.length > 0
      },
      hasMadeSelection() {
        return this.hasSelectedAnalyte && this.hasSelectedStation
      },
      // canDownload() {
      //   return this.isServiceActive != undefined && this.isServiceActive && (this.hasSelectedAnalyte && this.hasSelectedStation) && !this.loading
      // },
      canAsyncDownload() {
        return (this.hasSelectedAnalyte && this.hasSelectedStation) && !this.loading
      },
      canSave() {
        return this.isServiceActive != undefined && this.isServiceActive && (this.hasSelectedAnalyte && this.hasSelectedStation) && !this.loading
      },
      canNewQuery() {
        return !this.loading
      },
      loading() {
        return Object.values(this.loadingStates).some(isLoading => isLoading) || !this.isInitialized
      },
      hasUnsavedChanges() {
        return this.isServiceActive && this.hasMadeSelection && !this.isQuerySaved || this.mustSaveMissingParams
      },
    },
    watch: {
      missingParams() {
        this.mustSaveMissingParams = false
      },
    },
    methods: {
      pingService() {
        if (this.pingTimeoutId) return

        let ping = () => {
          this.$store.dispatch('multistation/pingService')
        }

        ping()
        this.pingTimeoutId = setInterval(ping, PING_INTERVAL)
      },
      updateSelection(selection) {
        this.selection = selection

        if (selection && selection.mediaTypes) {
          this.selectedMediaTypes = selection.mediaTypes
        }

        if (!this.isInitialized || this.loadingStates.selection) return
        this.isQuerySaved = false
      },
      applyMediaTypes: debounce(function(value) {
        if (this.$refs.viewQuery) {
          this.$refs.viewQuery.setMediaTypes([...value])
        }
      }, 300),
      resolveMediaTypes(queryMediaTypes) {
        if (queryMediaTypes && queryMediaTypes.length) {
          const matched = queryMediaTypes.filter(mt => this.allMediaTypes.includes(mt))
          if (matched.length) return matched
        }
        if (this.allMediaTypes.includes('Water')) return ['Water']
        if (this.allMediaTypes.length) return [this.allMediaTypes[0]]
        return []
      },
      updateQuery(query) {
        this.isQuerySaved = true
        this.mustSaveMissingParams = false
        this.query = query
        this.$refs.savedQueries.fetchQueries()
      },
      async clearQuery() {
        await this.loadQuery(emptyQuery)
        this.query = null
      },
      async fetchReportGenerator() {
        try {
          await this.$store.dispatch('multistation/fetchReportGenerator')
          this.pingService()
        } catch (error) {
          serviceUnavailable()
        }
      },
      async loadQuery(query) {
        if (this.loadingStates.selection) return
        this.isQuerySaved = true
        this.query = query

        const {
          selection,
          reportOptions,
        } = query.queryParams

        const mediaTypes = this.resolveMediaTypes(selection.mediaTypes)
        this.selectedMediaTypes = mediaTypes

        const selectionOptions = {
          relativeDate: query.relativeDate,
        }

        await this.$nextTick()
        this.$refs.reportParams.loadReportOptions(reportOptions)
        await this.$refs.viewQuery.loadSelection({
          ...selection,
          mediaTypes,
        }, selectionOptions)
      },
    },
    created() {
      this.fetchReportGenerator()
    },
    beforeDestroy() {
      clearTimeout(this.pingTimeoutId)
    },
  }
</script>

<style>
  .max-width {
    width: 100%;
  }

  .scroll-border-bottom {
    border-bottom-color: #ddd !important;
  }

  .align-button-checkbox {
    padding-left: 14px !important;
  }

  .align-button-checkbox>.v-btn:hover:before {
    background-color: transparent !important;
  }

  .align-button-checkbox-list {
    padding-left: 0px !important;
  }

  .align-button-checkbox-list>.v-btn {
    width: 22px;
    margin-right: 10px !important;
  }

  .align-button-checkbox-list>.v-btn:hover:before {
    background-color: transparent !important;
  }

  .selection-tab {
    background: #ddd;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    border: 1px solid rgba(0, 0, 0, .12);
  }
</style>